import React from 'react';
import { ArrowRight, Sparkles, Shield, Clock, DollarSign, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';
import { businessInfo } from '../utils/mock';
import { trackBookNowClick } from '../utils/analytics';

const Hero = () => {
  const scrollToBooking = () => {
    trackBookNowClick('hero');
    const element = document.getElementById('booking');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-32 md:pt-40">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1607860108855-64acf2078ed9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxjYXIlMjB3YXNoaW5nfGVufDB8fHx8MTc3NzUxOTc0NXww&ixlib=rb-4.1.0&q=85"
          alt="Professional Car Detailing"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50"></div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 md:px-12 lg:px-20 z-10">
        <div className="max-w-6xl mx-auto">
          {/* Badges Container - Centered */}
          <div className="flex flex-col items-center space-y-2 md:space-y-3 mb-4 md:mb-6">
            {/* Google Review Prompt - Top of Page */}
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-red-900/40 to-red-800/30 border border-red-500/30 rounded-full px-4 py-1.5 md:px-5 md:py-2 hover:border-red-500/50 transition-all cursor-pointer">
              <a
                href="https://g.page/r/CS8IeFV59cyJEBM/review"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2"
              >
                <span className="text-base md:text-lg">⭐</span>
                <span className="text-white font-semibold text-xs md:text-sm">Loved our service? Leave us a Google Review!</span>
              </a>
            </div>

            {/* First-Time Customer Offer Banner - Changed to Green */}
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full px-4 py-2 md:px-6 md:py-3 shadow-lg animate-pulse-slow">
              <Sparkles className="w-4 h-4 md:w-5 md:h-5 text-white" />
              <span className="text-white font-bold text-sm md:text-base">🎉 NEW CUSTOMERS: Save $30 on Your First Detail!</span>
            </div>
            
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-red-600/20 border border-red-500/50 rounded-full px-3 py-1.5 md:px-4 md:py-2">
              <Sparkles className="w-3 h-3 md:w-4 md:h-4 text-red-500" />
              <span className="text-red-500 font-semibold text-xs md:text-sm">Premium Mobile Detailing</span>
            </div>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl md:text-7xl font-bold text-white mb-4 md:mb-6 leading-tight text-center">
            {businessInfo.name}
            <span className="block text-red-500 mt-2">Detailing Services</span>
          </h1>

          {/* Subheading */}
          <p className="text-base md:text-2xl text-gray-300 mb-6 md:mb-8 leading-relaxed text-center">
            Professional detailing services that come to you. We bring showroom shine to your driveway.
          </p>

          {/* Features - More Compact on Mobile */}
          <div className="grid grid-cols-2 md:flex md:flex-wrap gap-3 md:gap-6 mb-6 md:mb-10 justify-center">
            <div className="flex items-center space-x-2 md:space-x-3">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-red-600/20 border border-red-500/50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 md:w-6 md:h-6 text-red-500" />
              </div>
              <div>
                <div className="text-white font-semibold text-sm md:text-base">Quality Guarantee</div>
                <div className="text-gray-400 text-xs md:text-sm">100% Satisfaction</div>
              </div>
            </div>
            <div className="flex items-center space-x-2 md:space-x-3">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-red-600/20 border border-red-500/50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-red-500" />
              </div>
              <div>
                <div className="text-white font-semibold text-sm md:text-base">Protects Your Vehicle</div>
                <div className="text-gray-400 text-xs md:text-sm">Long-lasting Protection</div>
              </div>
            </div>
            <div className="flex items-center space-x-2 md:space-x-3">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-red-600/20 border border-red-500/50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-red-500" />
              </div>
              <div>
                <div className="text-white font-semibold text-sm md:text-base">Restores Your Vehicle</div>
                <div className="text-gray-400 text-xs md:text-sm">Like-New Condition</div>
              </div>
            </div>
            <div className="flex items-center space-x-2 md:space-x-3">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-red-600/20 border border-red-500/50 rounded-lg flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-5 h-5 md:w-6 md:h-6 text-red-500" />
              </div>
              <div>
                <div className="text-white font-semibold">Great Value</div>
                <div className="text-gray-400 text-sm">Competitive Pricing</div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-red-600/20 border border-red-500/50 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <div className="text-white font-semibold">Convenient</div>
                <div className="text-gray-400 text-sm">We Come to You</div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-red-600/20 border border-red-500/50 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <div className="text-white font-semibold">Increases Value</div>
                <div className="text-gray-400 text-sm">Boosts Resale Price</div>
              </div>
            </div>
          </div>

          {/* CTAs - Centered */}
          <div className="flex flex-col sm:flex-row gap-4 mt-8 justify-center">
            <Button
              onClick={scrollToBooking}
              className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6 font-semibold group"
            >
              Book Your Detail
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              onClick={() => {
                const element = document.getElementById('services');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-black text-lg px-8 py-6 font-semibold"
            >
              View Services
            </Button>
          </div>

          {/* Value Increase Information Section - Moved below CTAs */}
          <div className="mt-8 p-6 bg-gradient-to-r from-red-950/40 to-red-900/30 border-2 border-red-500/30 rounded-xl">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-red-600/20 border border-red-500/50 rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h3 className="text-white font-bold text-xl mb-3">How Professional Detailing Increases Your Vehicle's Value</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2 mt-1">•</span>
                    <span><strong className="text-white">Maintains Paint Condition:</strong> Professional paint correction and protection prevent oxidation and fading</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2 mt-1">•</span>
                    <span><strong className="text-white">Protects Against UV Damage:</strong> Premium sealants and coatings shield your paint from sun damage and environmental contaminants</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2 mt-1">•</span>
                    <span><strong className="text-white">Preserves Interior Quality:</strong> Deep cleaning and conditioning keep upholstery, leather, and trim looking newer for longer</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2 mt-1">•</span>
                    <span><strong className="text-white">Faster Sales at Higher Prices:</strong> Industry studies show professionally detailed vehicles sell 2-3 weeks faster and command $500-$2,000+ more than comparable neglected vehicles</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2 mt-1">•</span>
                    <span><strong className="text-white">First Impressions Matter:</strong> A pristine vehicle signals excellent care and maintenance to potential buyers</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
